import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Card from './ui/Card';
import Button from './ui/Button';
import Spinner from './ui/Spinner';
import AdvisorIcon from './ui/AdvisorIcon';
import { supabase } from './supabaseClient';

const EXAMPLE_QUESTIONS = [
  'Kun je PP verlijmen? Zo ja, welk product is het beste?',
  'Kun je carbon verlijmen? Wat is hier belangrijk?',
  'Kan ik Trespa op een gevel verlijmen?',
  'Hoe kit ik een badkamer af?',
  'Kan Seal & Glue Strong onder water?',
  'Hoe verlijm ik EPDM?',
  'Wat gebruik ik om tandwielen te smeren?',
  'Welke kit kan ik bij een kookplaat gebruiken?'
];

// Generate a unique session ID for tracking
const SESSION_ID = `session_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

function FeedbackButtons({ messageIndex, question, answer, productIds, onFeedback, feedbackGiven }) {
  if (feedbackGiven !== null) {
    return (
      <div className="feedbackStatus">
        {feedbackGiven === 1 ? '👍 Bedankt voor je feedback!' : '👎 Bedankt, we leren hiervan!'}
      </div>
    );
  }

  return (
    <div className="feedbackButtons">
      <span className="feedbackButtons__label">Was dit nuttig?</span>
      <button
        className="feedbackBtn feedbackBtn--up"
        onClick={() => onFeedback(messageIndex, question, answer, productIds, 1)}
        title="Nuttig"
      >
        👍
      </button>
      <button
        className="feedbackBtn feedbackBtn--down"
        onClick={() => onFeedback(messageIndex, question, answer, productIds, -1)}
        title="Niet nuttig"
      >
        👎
      </button>
    </div>
  );
}

function MessageBubble({ message, isUser, messageIndex, question, productIds, onFeedback, feedbackGiven }) {
  return (
    <div className={`chatMsg ${isUser ? 'chatMsg--user' : 'chatMsg--ai'}`}>
      {!isUser && (
        <div className="chatMsg__avatar">
          <AdvisorIcon size={16} />
        </div>
      )}
      <div className="chatMsg__content">
        <div className="chatMsg__bubble">{message}</div>
        {!isUser && onFeedback && (
          <FeedbackButtons
            messageIndex={messageIndex}
            question={question}
            answer={message}
            productIds={productIds}
            onFeedback={onFeedback}
            feedbackGiven={feedbackGiven}
          />
        )}
      </div>
    </div>
  );
}

function ProductRecommendation({ product }) {
  return (
    <Link to={`/products/${product.id}`} className="prodRec">
      {product.image && <img src={product.image} alt="" className="prodRec__img" />}
      <div className="prodRec__info">
        <div className="prodRec__name">{product.name}</div>
        <div className="prodRec__meta">
          {product.categories} {product.price && `• €${product.price}`}
        </div>
      </div>
    </Link>
  );
}

export default function ProductAdvisor({ user }) {
  const chatHistoryEnabled = Boolean(supabase && user?.id);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [attachment, setAttachment] = useState(null); // { name, type, text }
  const [attachmentError, setAttachmentError] = useState('');
  const [feedbackMap, setFeedbackMap] = useState({}); // { messageIndex: rating }
  const [lastQuestion, setLastQuestion] = useState('');
  const [chatSessionId, setChatSessionId] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [busySessionId, setBusySessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const chatSessionIdRef = useRef(null);

  useEffect(() => {
    chatSessionIdRef.current = chatSessionId;
  }, [chatSessionId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Submit feedback to the API
  const submitFeedback = useCallback(async (messageIndex, question, answer, productIds, rating) => {
    // Update UI immediately
    setFeedbackMap(prev => ({ ...prev, [messageIndex]: rating }));

    try {
      await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          answer,
          productIds,
          rating,
          sessionId: SESSION_ID
        })
      });
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      // Don't show error to user, feedback is non-critical
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    let cancelled = false;

    const loadInitialSessions = async () => {
      if (!supabase || !user?.id) return;

      setLoadingSessions(true);
      setError('');

      try {
        const { data, error: sessionErr } = await supabase
          .from('chat_sessions')
          .select('id, title, created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(25);

        if (sessionErr) throw sessionErr;

        let list = data || [];
        if (!list.length) {
          const { data: created, error: createErr } = await supabase
            .from('chat_sessions')
            .insert({ user_id: user.id, title: 'Chat' })
            .select('id, title, created_at')
            .single();

          if (createErr) throw createErr;
          list = created ? [created] : [];
        }

        if (cancelled) return;
        setSessions(list);

        const initialId = list?.[0]?.id || null;
        if (initialId) {
          setChatSessionId(initialId);
          setLoadingHistory(true);
          const { data: rows, error: msgErr } = await supabase
            .from('chat_messages')
            .select('role, content')
            .eq('session_id', initialId)
            .order('created_at', { ascending: true });

          if (msgErr) throw msgErr;
          const loaded = (rows || []).map((r) => ({
            text: r.content,
            isUser: r.role === 'user'
          }));

          if (!cancelled) {
            setMessages(loaded);
            setFeedbackMap({});
            setRecommendedProducts([]);
            setLastQuestion('');
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError('Chatgeschiedenis kon niet worden geladen. Controleer Supabase tabellen en RLS.');
        }
      } finally {
        if (!cancelled) {
          setLoadingSessions(false);
          setLoadingHistory(false);
        }
      }
    };

    loadInitialSessions();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const persistMessage = async (sessionId, role, content) => {
    if (!supabase || !user?.id || !sessionId) return;
    try {
      await supabase.from('chat_messages').insert({
        session_id: sessionId,
        user_id: user.id,
        role,
        content: String(content || '').slice(0, 10000)
      });
    } catch {
      // ignore
    }
  };

  const ensureSession = async () => {
    if (!supabase || !user?.id) return null;
    const existing = chatSessionIdRef.current;
    if (existing) return existing;

    try {
      const { data: created, error: createErr } = await supabase
        .from('chat_sessions')
        .insert({ user_id: user.id, title: 'Nieuw gesprek' })
        .select('id, title, created_at')
        .single();

      if (createErr) throw createErr;

      const newId = created?.id || null;
      if (newId) {
        setChatSessionId(newId);
        setSessions((prev) => [created, ...(prev || [])]);
      }
      return newId;
    } catch {
      return null;
    }
  };

  const maybeUpdateSessionTitle = async (sessionId, question) => {
    if (!supabase || !user?.id || !sessionId) return;
    const current = sessions.find((s) => s.id === sessionId);
    const currentTitle = (current?.title || '').trim();
    if (currentTitle && currentTitle !== 'Chat' && currentTitle !== 'Nieuw gesprek') return;

    const nextTitle = String(question || '').trim().slice(0, 60) || 'Chat';
    try {
      const { error: updErr } = await supabase
        .from('chat_sessions')
        .update({ title: nextTitle })
        .eq('id', sessionId)
        .eq('user_id', user.id);

      if (!updErr) {
        setSessions((prev) =>
          (prev || []).map((s) => (s.id === sessionId ? { ...s, title: nextTitle } : s))
        );
      }
    } catch {
      // ignore
    }
  };

  const selectSession = async (sessionId) => {
    if (!supabase || !user?.id || !sessionId) return;
    if (loading) return;

    setError('');
    setLoadingHistory(true);
    setChatSessionId(sessionId);
    setMessages([]);
    setFeedbackMap({});
    setRecommendedProducts([]);
    setLastQuestion('');
    setAttachment(null);
    setAttachmentError('');

    try {
      const { data: rows, error: msgErr } = await supabase
        .from('chat_messages')
        .select('role, content')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (msgErr) throw msgErr;
      const loaded = (rows || []).map((r) => ({
        text: r.content,
        isUser: r.role === 'user'
      }));
      setMessages(loaded);
    } catch {
      setError('Kon dit gesprek niet laden.');
    } finally {
      setLoadingHistory(false);
    }
  };

  const beginRenameSession = (sessionId, currentTitle) => {
    if (loading || loadingHistory) return;
    setEditingSessionId(sessionId);
    setEditingTitle((currentTitle || 'Chat').toString());
  };

  const cancelRenameSession = () => {
    setEditingSessionId(null);
    setEditingTitle('');
  };

  const saveRenameSession = async (sessionId) => {
    if (!supabase || !user?.id || !sessionId) return;
    const title = String(editingTitle || '').trim().slice(0, 60);
    if (!title) return;

    setBusySessionId(sessionId);
    setError('');

    try {
      const { error: updErr } = await supabase
        .from('chat_sessions')
        .update({ title })
        .eq('id', sessionId)
        .eq('user_id', user.id);

      if (updErr) throw updErr;

      setSessions((prev) => (prev || []).map((s) => (s.id === sessionId ? { ...s, title } : s)));
      cancelRenameSession();
    } catch {
      setError('Kon gesprek niet hernoemen.');
    } finally {
      setBusySessionId(null);
    }
  };

  const deleteSession = async (sessionId) => {
    if (!supabase || !user?.id || !sessionId) return;
    if (loading || loadingHistory) return;

    const ok = window.confirm('Weet je zeker dat je dit gesprek wilt verwijderen?');
    if (!ok) return;

    setBusySessionId(sessionId);
    setError('');

    try {
      // Explicitly delete messages first to avoid FK issues in case CASCADE isn't applied.
      await supabase
        .from('chat_messages')
        .delete()
        .eq('session_id', sessionId)
        .eq('user_id', user.id);

      const { error: delErr } = await supabase
        .from('chat_sessions')
        .delete()
        .eq('id', sessionId)
        .eq('user_id', user.id);

      if (delErr) throw delErr;

      const remaining = (sessions || []).filter((s) => s.id !== sessionId);
      setSessions(remaining);

      if (chatSessionId === sessionId) {
        cancelRenameSession();
        setMessages([]);
        setFeedbackMap({});
        setRecommendedProducts([]);
        setLastQuestion('');

        const nextId = remaining?.[0]?.id || null;
        if (nextId) {
          await selectSession(nextId);
        } else {
          await startNewConversation();
        }
      }
    } catch {
      setError('Kon gesprek niet verwijderen.');
    } finally {
      setBusySessionId(null);
    }
  };

  const startNewConversation = async () => {
    if (!supabase || !user?.id || loading) return;

    setError('');
    setLoadingHistory(true);
    try {
      const { data: created, error: createErr } = await supabase
        .from('chat_sessions')
        .insert({ user_id: user.id, title: 'Nieuw gesprek' })
        .select('id, title, created_at')
        .single();

      if (createErr) throw createErr;

      setSessions((prev) => [created, ...(prev || [])]);
      setChatSessionId(created.id);
      setMessages([]);
      setFeedbackMap({});
      setRecommendedProducts([]);
      setLastQuestion('');
      cancelRenameSession();
      setAttachment(null);
      setAttachmentError('');
    } catch {
      setError('Kon geen nieuw gesprek starten.');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSubmit = async (questionOverride) => {
    const question = questionOverride || input.trim();
    if (!question || loading) return;

    const sessionId = await ensureSession();

    setInput('');
    setError('');
    const userMessageText = attachment ? `${question}\n\n📎 Bijlage: ${attachment.name}` : question;

    setMessages((prev) => [
      ...prev,
      {
        text: userMessageText,
        isUser: true
      }
    ]);
    if (sessionId) {
      persistMessage(sessionId, 'user', userMessageText);
      maybeUpdateSessionTitle(sessionId, question);
    }
    setLoading(true);

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          attachment: attachment
            ? {
                name: attachment.name,
                type: attachment.type,
                text: attachment.text,
                dataBase64: attachment.dataBase64
              }
            : null
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Er ging iets mis');
      }

      setMessages((prev) => [...prev, { text: data.answer, isUser: false }]);
      if (sessionId) persistMessage(sessionId, 'ai', data.answer);
      setRecommendedProducts(data.products || []);
      setLastQuestion(question);
      setAttachment(null);
      setAttachmentError('');
    } catch (err) {
      setError(err.message);
      setMessages((prev) => [
        ...prev,
        { text: `⚠️ Fout: ${err.message}`, isUser: false }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handlePickFile = () => {
    if (loading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';

    if (!file) return;

    setAttachmentError('');

    // Hard limits: keep payload small & fast
    // Note: PDFs/DOCX are base64-encoded and become ~33% larger in transit.
    const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
    if (file.size > MAX_BYTES) {
      setAttachment(null);
      setAttachmentError('Bestand is te groot (max 5 MB).');
      return;
    }

    const type = file.type || 'text/plain';
    const allowedText =
      type.startsWith('text/') ||
      type === 'application/json' ||
      type === 'application/xml' ||
      type === 'text/xml';

    const allowedBinary =
      type === 'application/pdf' ||
      type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    // Some browsers don't set type for .log/.md/.csv; allow by extension too.
    const nameLower = (file.name || '').toLowerCase();
    const allowedByExt = /\.(txt|md|markdown|csv|json|log|xml|pdf|docx)$/i.test(nameLower);

    if (!(allowedText || allowedBinary) && !allowedByExt) {
      setAttachment(null);
      setAttachmentError('Alleen .txt/.md/.csv/.json/.log/.xml en PDF/DOCX zijn toegestaan.');
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => {
      setAttachment(null);
      setAttachmentError('Kon het bestand niet lezen.');
    };

    // Text files: read as text
    if (allowedText || (!allowedBinary && allowedByExt && !/\.(pdf|docx)$/i.test(nameLower))) {
      reader.onload = () => {
        const text = String(reader.result || '');
        const MAX_CHARS = 12000;
        const trimmed = text.length > MAX_CHARS ? text.slice(0, MAX_CHARS) : text;
        setAttachment({ name: file.name, type, text: trimmed });
      };
      reader.readAsText(file);
      return;
    }

    // PDF/DOCX: read as base64 so the API can extract text server-side
    reader.onload = () => {
      const buffer = reader.result;
      if (!(buffer instanceof ArrayBuffer)) {
        setAttachment(null);
        setAttachmentError('Kon het bestand niet lezen.');
        return;
      }

      const bytes = new Uint8Array(buffer);
      let binary = '';
      const CHUNK = 0x8000;
      for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
      }

      const base64 = btoa(binary);
      setAttachment({ name: file.name, type, dataBase64: base64 });
    };
    reader.readAsArrayBuffer(file);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="advisorPage">
      <div className="advisorLayout">
        {/* Chat area */}
        <Card className="advisorChat">
          {/* Messages */}
          <div className="advisorChat__content">
            {messages.length === 0 ? (
              <div className="advisorWelcome">
                <div className="advisorWelcome__icon">
                  <AdvisorIcon size={28} />
                </div>
                <h2 className="advisorWelcome__title">Hoe kan ik je helpen?</h2>
                <p className="advisorWelcome__desc">
                  Stel een vraag over welk product je nodig hebt.<br />
                  Ik zoek in de Premtech catalogus en geef je een advies.
                </p>

                {/* Example questions */}
                <div className="advisorExamples">
                  <p className="advisorExamples__label">Probeer bijvoorbeeld:</p>
                  <div className="advisorExamples__grid">
                    {EXAMPLE_QUESTIONS.slice(0, 4).map((q, i) => (
                      <button
                        key={i}
                        onClick={() => handleSubmit(q)}
                        disabled={loading}
                        className="advisorExamples__btn"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="advisorMessages">
                {loadingHistory && (
                  <div className="advisorLoading">
                    <Spinner size={16} />
                    <span>Geschiedenis laden...</span>
                  </div>
                )}
                {messages.map((msg, i) => (
                  <MessageBubble 
                    key={i} 
                    message={msg.text} 
                    isUser={msg.isUser}
                    messageIndex={i}
                    question={!msg.isUser && i > 0 ? messages[i-1]?.text : null}
                    productIds={!msg.isUser ? recommendedProducts.map(p => p.id) : null}
                    onFeedback={!msg.isUser ? submitFeedback : null}
                    feedbackGiven={feedbackMap[i] ?? null}
                  />
                ))}
                {loading && (
                  <div className="advisorLoading">
                    <Spinner size={16} />
                    <span>Even denken...</span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input area */}
          <div className="advisorInput">
            <div className="advisorInput__row">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Typ je vraag hier..."
                disabled={loading}
                className="advisorInput__field"
              />

              <input
                ref={fileInputRef}
                type="file"
                className="advisorInput__file"
                onChange={handleFileChange}
                accept=".txt,.md,.markdown,.csv,.json,.log,.xml,.pdf,.docx,text/plain,text/markdown,text/csv,application/json,application/xml,text/xml,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              />

              <Button variant="ghost" size="sm" type="button" onClick={handlePickFile} disabled={loading}>
                Bestand
              </Button>

              <Button onClick={() => handleSubmit()} disabled={loading || !input.trim()}>
                Verstuur
              </Button>
            </div>

            {(attachment || attachmentError) && (
              <div className="advisorInput__meta">
                {attachment && (
                  <div className="advisorFileChip">
                    <span className="advisorFileChip__name">📎 {attachment.name}</span>
                    <button
                      type="button"
                      className="advisorFileChip__remove"
                      onClick={() => {
                        setAttachment(null);
                        setAttachmentError('');
                      }}
                      aria-label="Bijlage verwijderen"
                      disabled={loading}
                    >
                      ×
                    </button>
                  </div>
                )}
                {attachmentError && <div className="advisorInput__error">{attachmentError}</div>}
              </div>
            )}
          </div>
        </Card>

        {/* Right side */}
        {!chatHistoryEnabled && recommendedProducts.length === 0 ? (
          <Card className="advisorSidebar">
            <div className="advisorSidebar__header">
              <h3 className="advisorSidebar__title">Chatgeschiedenis</h3>
            </div>
            <div className="advisorSidebar__list">
              <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                Chatgeschiedenis staat pas aan als Supabase is geconfigureerd en je inlogt met je account.
                <br />
                <br />
                Controleer in Vercel: <b>REACT_APP_SUPABASE_URL</b> en <b>REACT_APP_SUPABASE_ANON_KEY</b>.
              </div>
            </div>
          </Card>
        ) : null}

        {chatHistoryEnabled || recommendedProducts.length > 0 ? (
          <div className="advisorSideStack">
            {chatHistoryEnabled && (
              <Card className="advisorSidebar advisorSidebar--sessions">
                <div className="advisorSidebar__header">
                  <div className="advisorSessionsHeader">
                    <h3 className="advisorSidebar__title">Mijn gesprekken</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={startNewConversation}
                      disabled={loading || loadingHistory}
                    >
                      Nieuw
                    </Button>
                  </div>
                </div>
                <div className="advisorSidebar__list">
                  {loadingSessions ? (
                    <div className="advisorLoading">
                      <Spinner size={16} />
                      <span>Gesprekken laden...</span>
                    </div>
                  ) : (
                    (sessions || []).map((s) => {
                      const active = s.id === chatSessionId;
                      const title = (s.title || 'Chat').trim() || 'Chat';
                      const created = s.created_at ? new Date(s.created_at) : null;
                      const meta = created && !Number.isNaN(created.getTime())
                        ? created.toLocaleString('nl-NL', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : '';

                      const isEditing = editingSessionId === s.id;
                      const isBusy = busySessionId === s.id;

                      return (
                        <div key={s.id} className={`advisorSessionRow ${active ? 'advisorSessionRow--active' : ''}`}>
                          <button
                            type="button"
                            className="advisorSessionMain"
                            onClick={() => selectSession(s.id)}
                            disabled={loading || loadingHistory || isBusy}
                            title={title}
                          >
                            {isEditing ? (
                              <input
                                className="advisorSessionEdit"
                                value={editingTitle}
                                onChange={(e) => setEditingTitle(e.target.value)}
                                maxLength={60}
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    saveRenameSession(s.id);
                                  }
                                  if (e.key === 'Escape') {
                                    e.preventDefault();
                                    cancelRenameSession();
                                  }
                                }}
                              />
                            ) : (
                              <>
                                <div className="advisorSessionItem__title">{title}</div>
                                {meta && <div className="advisorSessionItem__meta">{meta}</div>}
                              </>
                            )}
                          </button>

                          <div className="advisorSessionActions">
                            {isEditing ? (
                              <>
                                <button
                                  type="button"
                                  className="advisorSessionActionBtn"
                                  onClick={() => saveRenameSession(s.id)}
                                  disabled={isBusy || !String(editingTitle || '').trim()}
                                  title="Opslaan"
                                >
                                  ✓
                                </button>
                                <button
                                  type="button"
                                  className="advisorSessionActionBtn"
                                  onClick={cancelRenameSession}
                                  disabled={isBusy}
                                  title="Annuleren"
                                >
                                  ×
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="advisorSessionActionBtn"
                                  onClick={() => beginRenameSession(s.id, title)}
                                  disabled={loading || loadingHistory || isBusy}
                                  title="Hernoemen"
                                >
                                  ✎
                                </button>
                                <button
                                  type="button"
                                  className="advisorSessionActionBtn advisorSessionActionBtn--danger"
                                  onClick={() => deleteSession(s.id)}
                                  disabled={loading || loadingHistory || isBusy}
                                  title="Verwijderen"
                                >
                                  🗑
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </Card>
            )}

            {recommendedProducts.length > 0 && (
              <Card className="advisorSidebar">
                <div className="advisorSidebar__header">
                  <h3 className="advisorSidebar__title">Aanbevolen producten</h3>
                </div>
                <div className="advisorSidebar__list">
                  {recommendedProducts.map((p) => (
                    <ProductRecommendation key={p.id} product={p} />
                  ))}
                </div>
              </Card>
            )}
          </div>
        ) : null}
      </div>

      {error && (
        <div className="advisorError">
          {error}
        </div>
      )}
    </div>
  );
}
